import { useState } from 'react';
import { T, useGT } from 'gt-react';
import { PlusIcon, XIcon } from '@phosphor-icons/react';
import {
  ReportToneSchema,
  TableConditionalFormatSchema,
  type TableComponent,
  type TableConditionalFormat,
} from '@erp/shared-types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { ReportDatasetColumn } from '../ReportDataContext';
import { NumberFormatFields } from '../format/NumberFormatFields';
import { ConfigRow } from './shared';
import { TONE_STYLES } from './tableFormatting';
import { IconTooltip } from '@/components/common/IconTooltip';

type Threshold = Extract<TableConditionalFormat, { kind: 'threshold' }>;
const OPS: readonly Threshold['op'][] = [
  'gt',
  'gte',
  'lt',
  'lte',
  'eq',
  'neq',
  'between',
];

function newId(): string {
  return crypto.randomUUID();
}

function useOpLabels(): Record<Threshold['op'], string> {
  const gt = useGT();
  const context = 'table conditional format comparison';
  return {
    gt: gt('Greater than', { context }),
    gte: gt('At least', { context }),
    lt: gt('Less than', { context }),
    lte: gt('At most', { context }),
    eq: gt('Equals', { context }),
    neq: gt('Does not equal', { context }),
    between: gt('Between', { context }),
  };
}

function useToneLabels(): Record<Threshold['tone'], string> {
  const gt = useGT();
  const context = 'table conditional format tone';
  return {
    positive: gt('Good (green)', { context }),
    negative: gt('Bad (red)', { context }),
    warning: gt('Caution (amber)', { context }),
    neutral: gt('Neutral (gray)', { context }),
  };
}

function NumberInput({
  value,
  label,
  onChange,
}: {
  value: number | undefined;
  label: string;
  onChange: (value: number) => void;
}) {
  const [text, setText] = useState(value === undefined ? '' : String(value));
  return (
    <Input
      className="h-8 w-20 text-xs"
      inputMode="decimal"
      aria-label={label}
      value={text}
      onChange={(event) => {
        setText(event.target.value);
        const n = Number(event.target.value);
        if (event.target.value.trim() !== '' && Number.isFinite(n)) onChange(n);
      }}
    />
  );
}

function RuleRow({
  rule,
  columns,
  onChange,
  onRemove,
}: {
  rule: TableConditionalFormat;
  columns: readonly ReportDatasetColumn[];
  onChange: (rule: TableConditionalFormat) => void;
  onRemove: () => void;
}) {
  const gt = useGT();
  const ops = useOpLabels();
  const tones = useToneLabels();
  return (
    <div className="space-y-1.5 rounded-md border p-2">
      <div className="flex items-center gap-1.5">
        <Select
          value={rule.column}
          onValueChange={(column) => onChange({ ...rule, column })}
        >
          <SelectTrigger
            className="h-8 min-w-0 flex-1 text-xs"
            aria-label={gt('Column', { context: 'table conditional format' })}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {columns.map((column) => (
              <SelectItem key={column.key} value={column.key}>
                {column.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <IconTooltip>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0"
            aria-label={gt('Remove this rule', {
              context: 'table conditional format',
            })}
            onClick={onRemove}
          >
            <XIcon className="h-4 w-4" />
          </Button>
        </IconTooltip>
      </div>
      {rule.kind === 'color-scale' ? (
        <p className="text-xs text-muted-foreground">
          <T context="table conditional format color scale">
            Color scale from the report palette's sequential colors, lowest to
            highest. A rule that matches a cell wins over the scale.
          </T>
        </p>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-1.5">
            <Select
              value={rule.op}
              onValueChange={(op) =>
                onChange({ ...rule, op: OPS.find((o) => o === op) ?? rule.op })
              }
            >
              <SelectTrigger
                className="h-8 w-32 text-xs"
                aria-label={gt('Comparison', {
                  context: 'table conditional format',
                })}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {OPS.map((op) => (
                  <SelectItem key={op} value={op}>
                    {ops[op]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <NumberInput
              value={rule.value}
              label={gt('Value', { context: 'table conditional format' })}
              onChange={(value) => onChange({ ...rule, value })}
            />
            {rule.op === 'between' && (
              <NumberInput
                value={rule.value2}
                label={gt('And', { context: 'table conditional format' })}
                onChange={(value2) => onChange({ ...rule, value2 })}
              />
            )}
          </div>
          <Select
            value={rule.tone}
            onValueChange={(tone) =>
              onChange({ ...rule, tone: ReportToneSchema.parse(tone) })
            }
          >
            <SelectTrigger
              className="h-8 text-xs"
              aria-label={gt('Color', { context: 'table conditional format' })}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ReportToneSchema.options.map((tone) => (
                <SelectItem key={tone} value={tone}>
                  <span className="flex items-center gap-2">
                    <span
                      className="h-3 w-3 rounded-sm border"
                      style={{
                        backgroundColor: TONE_STYLES[tone].backgroundColor,
                      }}
                      aria-hidden
                    />
                    {tones[tone]}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </>
      )}
    </div>
  );
}

/**
 * A Table's number formats by column and its conditional formats, in the
 * Report Builder: the Number columns only, since both read Numbers.
 */
export function TableFormattingFields({
  component,
  columns,
  onUpdate,
}: {
  component: TableComponent;
  columns: readonly ReportDatasetColumn[];
  onUpdate: (updated: TableComponent) => void;
}) {
  const gt = useGT();
  const numeric = columns.filter((column) => column.dataType === 'number');
  const [picked, setFormatColumn] = useState('');
  if (!numeric.length) return null;
  const first = numeric[0]?.key ?? '';
  // The pick while its column is still a Number column, else the first one:
  // columns load (and datasets change) after the fields mount.
  const formatColumn = numeric.some((column) => column.key === picked)
    ? picked
    : first;
  const rules = component.conditionalFormats;
  const setRules = (next: TableConditionalFormat[]) =>
    onUpdate({ ...component, conditionalFormats: next });
  const add = (kind: TableConditionalFormat['kind']) =>
    setRules([
      ...rules,
      TableConditionalFormatSchema.parse(
        kind === 'threshold'
          ? {
              id: newId(),
              kind,
              column: first,
              op: 'lt',
              value: 0,
              tone: 'negative',
            }
          : { id: newId(), kind, column: first },
      ),
    ]);
  return (
    <>
      <ConfigRow label={gt('Number format', { context: 'report table' })}>
        <Select value={formatColumn} onValueChange={setFormatColumn}>
          <SelectTrigger
            className="h-8 text-xs"
            aria-label={gt('Column to format', { context: 'report table' })}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {numeric.map((column) => (
              <SelectItem key={column.key} value={column.key}>
                {column.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {formatColumn && (
          <NumberFormatFields
            key={formatColumn}
            idPrefix={`table-${component.id}-format`}
            value={component.columnFormats[formatColumn]}
            onChange={(format) =>
              onUpdate({
                ...component,
                columnFormats: {
                  ...component.columnFormats,
                  [formatColumn]: format,
                },
              })
            }
          />
        )}
      </ConfigRow>
      <ConfigRow
        label={gt('Conditional formatting', { context: 'report table' })}
      >
        <div className="space-y-2">
          {rules.map((rule, index) => (
            <RuleRow
              key={rule.id}
              rule={rule}
              columns={numeric}
              onChange={(next) =>
                setRules(rules.map((r, i) => (i === index ? next : r)))
              }
              onRemove={() => setRules(rules.filter((_, i) => i !== index))}
            />
          ))}
          <div className="flex gap-1.5">
            <Button
              variant="outline"
              size="sm"
              className="h-7 gap-1 text-xs"
              onClick={() => add('threshold')}
            >
              <PlusIcon className="h-3.5 w-3.5" />
              <T context="table conditional format add threshold">Rule</T>
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-7 gap-1 text-xs"
              onClick={() => add('color-scale')}
            >
              <PlusIcon className="h-3.5 w-3.5" />
              <T context="table conditional format add color scale">
                Color scale
              </T>
            </Button>
          </div>
        </div>
      </ConfigRow>
    </>
  );
}
